<template>
  <section class="homelab-statistics">
    <Breadcrumbs
      :breadcrumbs="[
        { title: $t('menu.homelab'), disabled: true },
        { title: $t('homelab.statistics'), disabled: true }
      ]">
    </Breadcrumbs>

    <div class="evollu-form row">

      <div class="col-12 col-sm-6 col-md-4">
        <label class="form-label">
          {{$t('homelab.labels.entities')}}
        </label>
        <multiselect
          id="entities-or-clusters-multiselect"
          v-model="filters.entities"
          :options="entityOptions"
          track-by="uid"
          :customLabel="formatedEntityLabel"
          :placeholder="$t('homelab.labels.accumulated_values')"
          :allow-empty="true"
          :multiple="true"
          >
          <template slot="clear">
            <span class="multiselect__clear" v-if="filters.entities.length > 0" @mousedown.prevent.stop="filters.entities = []"></span>
          </template>
        </multiselect>
      </div>

      <div class="col-12 col-sm-6 col-md-4">
        <label class="form-label">{{ $t('homelab.tables.scheduled_time') }}</label>
        <range-date-picker
          id="statistics-date-range"
          v-model="filters.dateRange"
          placeholder="---"
          :clearable="false"
        >
        </range-date-picker>
      </div>

    </div>

    <div class="py-3">

      <div class="row">
        <div class="col-12 col-lg-6">
          <horizontal-donut-chart-widget
            :title="$t('homelab.labels.appointments_by_type')"
            :data="appointmentTypesChartData"
          >
          </horizontal-donut-chart-widget>
        </div>
        <div class="col-12 col-lg-6">
          <SimpleKPIWidget
            :title="$t('homelab.labels.avg_appointments_per_day')"
            :value="averageAppointmentsPerDay"
            :label="$t('homelab.labels.avg_appointments')"
          >
          </SimpleKPIWidget>
        </div>
      </div>

      <div class="row">
        <div class="col-12 col-lg-6">
          <horizontal-donut-chart-widget
            :title="$t('homelab.labels.pending_appointments')"
            :data="appointmentsChartData"
          >
          </horizontal-donut-chart-widget>
        </div>
        <div class="col-12 col-lg-6">
          <horizontal-donut-chart-widget
            :title="$t('homelab.labels.in_execution')"
            :data="executionChartData"
          >
          </horizontal-donut-chart-widget>
        </div>
      </div>
    </div>
  </section>
</template>
<script>
import { mapGetters } from 'vuex'
import Multiselect from 'vue-multiselect'
import { AppointmentService } from '@/api/HomelabService'
import Breadcrumbs from '@/components/common/Breadcrumbs'
import RangeDatePicker from '@/components/common/RangeDatePicker'
import LocalStorageHelper from '@/local-storage/LocalStorageHelper'
import { HOMELAB_STATISTICS } from '@/local-storage/LocalStorageKeys'
import ClustersAndEntitiesMixin from '@/mixins/ClustersAndEntitiesMixin'
import MultiselectLabelFormatter from '@/mixins/MultiselectLabelFormatter'
import SimpleKPIWidget from '@/components/common/statistics/SimpleKPIWidget'
import HorizontalDonutChartWidget from '@/components/common/statistics/HorizontalDonutChartWidget'

export default {
  components: {
    Breadcrumbs,
    Multiselect,
    RangeDatePicker,
    HorizontalDonutChartWidget,
    SimpleKPIWidget
  },
  mixins: [
    MultiselectLabelFormatter,
    ClustersAndEntitiesMixin
  ],
  props: {
    component: {
      type: Object,
      required: true
    }
  },
  computed: {
    ...mapGetters([
      'clusters',
    ]),
    homelabService: function () {
      return this.component.service
    },
    readableEntities: function () {
      let appointmentPermissions = this.homelabService.user_permissions.filter(permission => permission.access_type >= 1 && permission.info_type === 100)
      if (appointmentPermissions.length > 0) {
        if (appointmentPermissions.some(permission => permission.entity_id === null)) {
          return [...this.homelabService.entities]
        } else {
          return appointmentPermissions.map(permission => this.homelabService.entities.find(entity => permission.entity_id === entity.id)).filter(v => v)
        }
      }
    },
    entityOptions: function () {
      return this.createClustersAndEntitiesList(this.clusters, this.readableEntities)
    },
    filteredData: function () {
      return this.selectedEntityIds.length ? this.originalData.filter(element => this.selectedEntityIds.includes(element.entity_id)) : this.originalData
    },
    averageAppointmentsPerDay: function () {
      let appointmentsInsidePeriod = this.filteredData.reduce((accumulator, value) => accumulator + value.appointmentsInsidePeriod, 0)
      let start = this.moment(this.filters.dateRange.start)
      let end = this.moment(this.filters.dateRange.end)
      let daysBetweenDates = end.diff(start, 'days') || 1
      return appointmentsInsidePeriod / daysBetweenDates
    },
    appointmentsChartData: function () {
      let requestedCount = this.filteredData.reduce((accumulator, value) => accumulator + value.requested, 0)
      let confirmedCount = this.filteredData.reduce((accumulator, value) => accumulator + value.confirmed, 0)
      let collectionUnsuccessfulCount = this.filteredData.reduce((accumulator, value) => accumulator + value.collectionUnsuccessful, 0)
      let canceledCount = this.filteredData.reduce((accumulator, value) => accumulator + value.canceled, 0)

      return {
        labels: [
          this.$t('homelab.labels.requested_appointments'),
          this.$t('homelab.labels.confirmed_appointments'),
          this.$t('homelab.labels.collection_unsuccessful_appointments'),
          this.$t('homelab.labels.canceled_appointments'),
        ],
        datasets: [{
          data: [requestedCount, confirmedCount, collectionUnsuccessfulCount, canceledCount],
          backgroundColor: [
            '#ffc000',
            '#17a6aa',
            '#eb5257',
            '#69120c',
          ]
        }]
      }
    },
    executionChartData: function () {
      let collectionSuccessfulCount = this.filteredData.reduce((accumulator, value) => accumulator + value.collectionSuccessful, 0)
      let readyPayedCount = this.filteredData.reduce((accumulator, value) => accumulator + value.readyPayed, 0)
      let readyUnpayedCount = this.filteredData.reduce((accumulator, value) => accumulator + value.readyUnpayed, 0)

      return {
        labels: [
          this.$t('homelab.labels.collection_successful_appointments'),
          this.$t('homelab.labels.ready_unpayed_appointments'),
          this.$t('homelab.labels.results_ready_payed_appointments')
        ],
        datasets: [{
          data: [collectionSuccessfulCount, readyUnpayedCount, readyPayedCount],
          backgroundColor: [
            '#ffc000',
            '#2ced89',
            '#17a6aa',
          ]
        }]
      }
    },
    appointmentTypesChartData: function () {
      let p1 = this.filteredData.reduce((accumulator, value) => accumulator + value.perType.p1, 0)
      let insurer = this.filteredData.reduce((accumulator, value) => accumulator + value.perType.insurer, 0)
      let packs = this.filteredData.reduce((accumulator, value) => accumulator + value.perType.packs, 0)
      let packsWithMedicalReport = this.filteredData.reduce((accumulator, value) => accumulator + value.perType.packsWithMedicalReport, 0)

      return {
        labels: [
          this.$t('homelab.labels.per_type_p1'),
          this.$t('homelab.labels.per_type_insurer'),
          this.$t('homelab.labels.per_type_packs'),
          this.$t('homelab.labels.per_type_packs_with_medical_report'),
        ],
        datasets: [{
          data: [p1, insurer, packs, packsWithMedicalReport],
          backgroundColor: [
            '#17a6aa',
            '#ffc000',
            '#eb5257',
            '#69120c',
          ]
        }]
      }
    },
  },
  data: function () {
    return {
      filters: {
        dateRange: {
          start: this.moment().subtract(1, 'months').format('YYYY-MM-DD'),
          end: this.moment().format('YYYY-MM-DD')
        },
        entities: []
      },
      selectedEntityIds: [],
      selectedEntityUids: [],
      originalData: []
    }
  },
  created: function () {
    this.$store.dispatch('homelab/INITIALIZE_MODULE')
    this.loadLocalStorage()
      .then(() => this.loadData())
  },
  methods: {
    loadData: function () {
      AppointmentService.getStatistics({
        start: this.filters.dateRange.start,
        end: this.filters.dateRange.end,
        dateType: 'created_at',
      })
        .then(data => {
          this.originalData = data
        })
        .catch(errors => {
          console.log(errors)
          this.showToast(this.$t('homelab.something_went_wrong'), this.$store.state.errorToastOptions)
        })
    },
    loadLocalStorage: function () {
      return new Promise((resolve, reject) => {
        const state = LocalStorageHelper.get(HOMELAB_STATISTICS)
        if (state) {
          if (Array.isArray(state.selectedEntityUids) && state.selectedEntityUids) {
            this.filters.entities = this.reloadSelectedClustersAndEntities(this.entityOptions, state.selectedEntityUids)
          }

          if (state.dateRange && state.dateRange.start && state.dateRange.start !== '' && state.dateRange.end && state.dateRange.end !== '') {
            this.filters.dateRange.start = state.dateRange.start
            this.filters.dateRange.end = state.dateRange.end
          }
        }

        this.hasLoadedLocalStorage = true

        resolve()
      })
    },
    saveLocalStorage: function () {
      LocalStorageHelper.set(HOMELAB_STATISTICS, {
        dateRange: this.filters.dateRange,
        selectedEntityUids: this.selectedEntityUids
      })
    }
  },
  watch: {
    'filters.entities': function (options) {
      const selectedOptions = this.getSelectedOptionsIdsAndUids(options)
      this.selectedEntityUids = selectedOptions.uids
      this.selectedEntityIds = selectedOptions.ids
      this.saveLocalStorage()
    },
    'filters.dateRange': function (value) {
      if (this.hasLoadedLocalStorage) {
        this.loadData()
      }

      this.saveLocalStorage()
    }
  }
}
</script>
